import { useState, useEffect } from "react";
import { fetchCollection, fetchPageBySlug } from "../lib/cms";
import Lightbox from "../components/Lightbox";

export default function Gallery() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("alle");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [page, allGalleries] = await Promise.all([
          fetchPageBySlug("galleri"),
          fetchCollection("galleries")
        ]);
        setPageData(page);
        setGalleries(allGalleries);
      } catch (error) {
        console.error("Fejl ved indlæsning af galleri data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Saml alle billeder til visning
  const allImages = galleries.flatMap((g) =>
    g.images.map((img) => ({
      src: img,
      galleryId: g.slug_id,
      galleryTitle: g.title
    }))
  );

  // Filtrerede billeder
  const filteredImages =
    activeFilter === "alle"
      ? allImages
      : allImages.filter((img) => img.galleryId === activeFilter);

  // Liste over unikke filtre/gallerier
  const filters = [
    { id: "alle", label: "Vis alle" },
    ...galleries.map((g) => ({ id: g.slug_id, label: g.title }))
  ];

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/6636b033-55a3-4308-bdc0-fd30994d19e2.jpg"
            alt="Galleri"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Galleri"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Se billeder fra vores moderne autoværksted, vores dygtige mekanikere og det professionelle udstyr, vi bruger til at reparere din bil.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Intro Text */}
          <div className="mx-auto max-w-3xl text-center space-y-6 mb-12">
            <div
              className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6 text-left md:text-center"
              dangerouslySetInnerHTML={{
                __html: pageData?.content || `
                  <p>Her kan du se et udvalg af billeder fra vores moderne autoværksted på Ribevej 26 i Skærbæk.</p>
                  <p>Vi har moderne faciliteter, professionelt udstyr og et velfungerende værksted med many lifte, så vi kan håndtere alle typer opgaver effektivt og sikkert.</p>
                `
              }}
            />
          </div>

          {loading ? (
            <div className="space-y-8">
              {/* Filter Skeleton */}
              <div className="flex justify-center gap-2 animate-pulse">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-10 w-24 bg-slate-200 rounded-md" />
                ))}
              </div>
              {/* Grid Skeleton */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 animate-pulse">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="aspect-video bg-slate-200 rounded-lg" />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Filter Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {filters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => {
                      setActiveFilter(filter.id);
                      setLightboxIndex(null);
                    }}
                    className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-md transition-all active:scale-95 ${
                      activeFilter === filter.id
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/10"
                        : "border border-slate-200 bg-slate-50 text-slate-600 hover:text-slate-900 hover:bg-slate-100 shadow-sm"
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Image Grid */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {filteredImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className="group relative aspect-video overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-1.5 hover:border-blue-500/30 transition-all active:scale-98 shadow-sm"
                  >
                    <img
                      src={image.src}
                      alt={`Galleri ${image.galleryTitle} ${index + 1}`}
                      className="h-full w-full rounded-lg object-cover transition-transform duration-300 group-hover:scale-103"
                      loading="lazy"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-1.5 rounded-lg bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 bg-slate-950/80 px-2 py-1 rounded border border-slate-800">
                        {image.galleryTitle}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {filteredImages.length === 0 && (
                <div className="py-12 text-center text-slate-500">
                  Ingen billeder fundet i denne kategori.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Integration */}
      {filteredImages.length > 0 && lightboxIndex !== null && (
        <Lightbox
          images={filteredImages.map((img) => img.src)}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </div>
  );
}
